import { Web3 } from 'web3';

/**
 * Tests a single WebSocket node's connectivity and latency
 * @param {string} nodeUrl - WebSocket node URL to test
 * @returns {Promise<Object>} Test results with connection status, latency, and block height
 */
const testWsNode = async (nodeUrl) => {
  return new Promise((resolve) => {
    try {
      const startTime = performance.now();
      const tempWeb3 = new Web3(nodeUrl);
      console.log('测试节点', nodeUrl);
      // Set timeout
      const timeout1 = setTimeout(() => {
        console.log('连接超时', nodeUrl);
        resolve({ connected: false, latency: Infinity, error: '连接超时', blockHeight: 0 });
      }, 5000);

      // Get block height and test connection
      Promise.all([
        tempWeb3.eth.getBlockNumber().catch(err => 0),
        tempWeb3.eth.subscribe('newBlockHeaders')
      ])
      .then(([blockHeight, subscription]) => {
          clearTimeout(timeout1);
        // Measure latency
        const latency = Math.round(performance.now() - startTime);
        console.log('连接成功', nodeUrl, latency);
        // Unsubscribe after successful test
        const timeout = setTimeout(() => {
          console.log('取消订阅超时', nodeUrl);
          resolve({ connected: true, latency: latency, error: null, blockHeight });
        }, 3000);
        subscription.unsubscribe()
          .then(() => {
            console.log('取消订阅成功', nodeUrl);
            resolve({ connected: true, latency, error: null, blockHeight });
          })
          .catch(() => {
            console.log('取消订阅失败', nodeUrl);
            // Even if unsubscribe fails, connection test is successful
            resolve({ connected: true, latency, error: null, blockHeight });
          });
      })
      .catch(error => {
        clearTimeout(timeout1);
        resolve({ connected: false, latency: Infinity, error: error.message, blockHeight: 0 });
      });
    } catch (error) {
      resolve({ connected: false, latency: Infinity, error: error.message, blockHeight: 0 });
    }
  });
};

/**
 * Tests multiple WebSocket nodes and returns a Web3 instance using the best one
 * @param {Object} logger - Optional logger object with addLog method
 * @returns {Promise<Web3>} Web3 instance connected to the best WebSocket node
 */
export const getFastestWsWeb3 = async (logger = null) => {
  // WebSocket node list
  const wsNodes = [
    'wss://bsc-rpc.publicnode.com',
    'wss://bsc.callstaticrpc.com',
    'wss://rpc-bsc.48.club/ws',
    'wss://bsc-ws-node.nariox.org:443',
    'wss://binance.llamarpc.com/ws',
  ];

  const addLog = (message) => {
    if (logger && typeof logger.addLog === 'function') {
      logger.addLog(message);
    } else {
      console.log(message);
    }
  };

  addLog('开始测试WebSocket节点连通性和延迟...');

  const nodeResults = {};
  const testPromises = [];

  // Test each node
  for (const nodeUrl of wsNodes) {
    const testPromise = testWsNode(nodeUrl)
      .then(result => {
        nodeResults[nodeUrl] = result;
      })
      .catch(error => {
        nodeResults[nodeUrl] = { connected: false, latency: Infinity, error: error.message, blockHeight: 0 };
      });

    testPromises.push(testPromise);
  }

  // Wait for all tests to complete
  await Promise.all(testPromises);

  // Find the highest block height among connected nodes
  let highestBlockHeight = 0;
  for (const [, result] of Object.entries(nodeResults)) {
    if (result.connected && result.blockHeight > highestBlockHeight) {
      highestBlockHeight = result.blockHeight;
    }
  }

  // Select the node with the highest block height and lowest latency
  let bestNode = null;
  let lowestLatency = Infinity;

  for (const [nodeUrl, result] of Object.entries(nodeResults)) {
    if (result.connected && result.blockHeight === highestBlockHeight && result.latency < lowestLatency) {
      bestNode = nodeUrl;
      lowestLatency = result.latency;
    }
  }

  if (bestNode) {
    addLog(`选择区块高度最高(${highestBlockHeight})且延迟最低的节点: ${bestNode} (${lowestLatency}ms)`);
    return new Web3(bestNode);
  } else {
    // If all nodes fail, use default
    const isHttps = typeof window !== 'undefined' && window.location.protocol === 'https:';
    const wsProtocol = isHttps ? 'wss' : 'ws';
    const wsPort = isHttps ? '443' : '80';
    const defaultNode = `${wsProtocol}://bsc-ws-node.nariox.org:${wsPort}`;

    addLog(`所有节点测试失败，使用默认节点: ${defaultNode}`);
    return new Web3(defaultNode);
  }
};

export default getFastestWsWeb3; 